import { useEffect } from 'react';
import { MDText } from 'i18n-react';
import { useRouter } from 'next/router';

import cypressIds from 'Components/cypress_ids.json';
import { logger } from 'Services/logger/logger';
import { resolveMakeModelFromMakeModelStores } from 'Stores/InsightsStore/InsightsStore.helpers';
import { InsightsStoreProvider } from 'Stores/InsightsStore/InsightsStoreContext';
import InsightsStoreUrlSynchronizer from 'Stores/InsightsStore/InsightsStoreUrlSynchronizer';
import useMakesStore from 'Stores/MakesStore/MakesStore';
import useModelsStore from 'Stores/ModelsStore/ModelsStore';
import { LIST_POSITION_PAGES } from 'Stores/VehicleListPositionStore/VehicleListPositionsStore.consts';
import type { Make } from 'Types/make';
import type { Model } from 'Types/model';
import { applyCypressData, isSSR } from 'Utilities/helpers';
import useVehicleListPosition from 'Utilities/hooks/vehicleList/useVehicleListPosition/useVehicleListPosition';

import { useInsightsRouter } from '../hooks/useInsightsRouter';

import { MetaTitle } from './components/MetaTitle/MetaTitle';
import { RecentSoldPrices } from './components/RecentSoldPrices/RecentSoldPrices';
import { Topbar } from './components/Topbar/Topbar';
import { isMakeAndModelChange } from './ResultsPage.helpers';
import LocalTexts from './ResultsPage.texts.json';

import styles from './ResultsPage.module.scss';

const LocalT = new MDText(LocalTexts);

export type InsightsResultsProps = {
	make?: string | string[];
	makes: Make[];
	model?: string | string[];
	models: Model[];
};

export const ResultsPage: React.FC<InsightsResultsProps> = ({
	make: makeServerSideQuery,
	makes: makesServerSideState,
	model: modelServerSideQuery,
	models: modelsServerSideState,
}) => {
	const makesStoreState = useMakesStore((state) => state.makes);
	const modelStoreState = useModelsStore((state) => state.models);
	const { goToLandingPage, vehicle } = useInsightsRouter();
	const { asPath, events } = useRouter();

	useVehicleListPosition(false, LIST_POSITION_PAGES.insights);

	useEffect(() => {
		const handleRouteChange = (route: string) => {
			// We want to scroll to the top of the page only when user changes the make and model in the Insights results page URL
			if (isMakeAndModelChange(route, asPath)) {
				setTimeout(() => {
					window.scrollTo({ behavior: 'smooth', top: 0 });
				}, 0);
			}
		};

		events.on('routeChangeStart', handleRouteChange);

		return () => {
			events.off('routeChangeStart', handleRouteChange);
		};
	}, [events, asPath]);

	const { make: makeClientSideQuery, model: modelClientSideQuery } = vehicle ?? {};

	let make;
	let model;

	try {
		const { make: resolvedMake, model: resolvedModel } = resolveMakeModelFromMakeModelStores({
			make: makeServerSideQuery || makeClientSideQuery,
			makes: makesServerSideState || makesStoreState,
			model: modelServerSideQuery || modelClientSideQuery,
			models: modelsServerSideState || modelStoreState,
		});

		make = resolvedMake;
		model = resolvedModel;
	} catch (error) {
		logger.error({
			error: error as Error,
			message: 'Error resolving make and model from stores',
			scope: 'InsightsResults',
		});
		if (!isSSR()) {
			void goToLandingPage({
				toast: {
					content: LocalT.translate(`makeAndModelError.content`),
					heading: LocalT.translate(`makeAndModelError.heading`),
					variant: 'error',
				},
			});
		}
		return null;
	}

	return (
		<InsightsStoreProvider initialValues={{ make, model }}>
			<MetaTitle />
			<InsightsStoreUrlSynchronizer>
				<main {...applyCypressData(cypressIds.containers.insightsResults)} className={styles.background}>
					<Topbar />
					<RecentSoldPrices />
				</main>
			</InsightsStoreUrlSynchronizer>
		</InsightsStoreProvider>
	);
};
